import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";
import secureLogger from "../../security/secureLogger";

const API_URL = `${API_BASE_URL}/api/PatientInfoV2/patient-map`;

/**
 * Obtiene el roster paginado del Patient Map (V2) desde la nueva base
 * `DB_MCM_SANTEPLUS` ejecutando el SP `patinfo.SP_GET_PATIENT_MAP`.
 *
 * Cambios respecto a la versión legacy (`/api/PatientInfo/Patientmap`):
 *  - Se elimina el flag `filterHighCost` y el campo `isHighCost` del response.
 *  - Se reemplaza `carePlansCount` por `careGaps` (Care Gaps del año actual).
 *  - Se agrega el filtro `dateOfBirth` (match exacto YYYY-MM-DD sobre DAT_DOB).
 *  - El response **no** trae `total`; usa `data.length` vs `pageSize` para
 *    estimar si hay página siguiente.
 *
 * @param {Object} [filters]
 * @param {number|null} [filters.locationId]
 * @param {string|null} [filters.memberType]    - "ACA" | "MED" | ...
 * @param {string|null} [filters.pcpName]       - LIKE 'valor%' sobre el nombre completo del PCP.
 * @param {string|null} [filters.gender]        - "M" | "F" | ...
 * @param {number|null} [filters.ageFrom]
 * @param {number|null} [filters.ageTo]
 * @param {string|null} [filters.dateOfBirth]   - "YYYY-MM-DD". Match exacto sobre DAT_DOB.
 * @param {string|null} [filters.memberId]
 * @param {string|null} [filters.memberFullName] - LIKE '%valor%'. Se envía en mayúsculas.
 * @param {boolean|null} [filters.filterAdmitted]
 * @param {boolean|null} [filters.filterDischarged]
 * @param {boolean|null} [filters.filterEmergency]
 * @param {boolean|null} [filters.filterHighRisk]
 * @param {number} [filters.pageNumber=1]
 * @param {number} [filters.pageSize=50]
 *
 * Ejemplo de llamada:
 * GET /api/PatientInfoV2/patient-map?pageNumber=1&pageSize=10
 * GET /api/PatientInfoV2/patient-map?locationId=4&memberType=ACA&ageFrom=18&ageTo=50&filterEmergency=true
 *
 * Respuesta exitosa (200):
 * {
 *   "data": [
 *     {
 *       "cipId": "43750135999",
 *       "memberType": "ACA",
 *       "memberId": "002W24287",
 *       "memberFullName": "DRAYTON, SHERRI A",
 *       "dateOfBirth": "1968-07-11T00:00:00",
 *       "age": 57,
 *       "ageRange": "50-plus",
 *       "gender": "F",
 *       "email": "SDRAY711@AOL.COM",
 *       "phoneNumber": "",
 *       "mobileNumber": "(954) 806-7347",
 *       "status": 1,
 *       "pcpName": "Orly Pena",
 *       "pcpTitle": "PCP",
 *       "locationId": 11,
 *       "medicalCenter": "Stuart",
 *       "locationAddress": "1050 SE MONTEREY RD STE 201, Stuart, FL - 34994, Martin",
 *       "careGaps": 0,
 *       "isAdmitted": 0,
 *       "isDischarged": 0,
 *       "isEmergency": 0,
 *       "isHighRisk": 0
 *     }
 *   ],
 *   "page": 1,
 *   "pageSize": 10
 * }
 *
 * Notas sobre `status` y flags clínicos:
 *  - `status` es int (1 = activo, 0 = inactivo) — no booleano.
 *  - `isAdmitted` / `isDischarged` / `isEmergency` / `isHighRisk` también
 *    se devuelven como int 0/1 para mantener compatibilidad con el V1.
 *
 * Códigos de respuesta:
 *  - 200 OK            → al menos un paciente en la página.
 *  - 204 No Content    → consulta válida pero página vacía (data viene undefined).
 *  - 401 Unauthorized  → JWT inválido o ausente.
 */
export const getPatientMapV2 = async ({
  locationId,
  memberType,
  pcpName,
  gender,
  ageFrom,
  ageTo,
  dateOfBirth,
  memberId,
  memberFullName,
  filterAdmitted,
  filterDischarged,
  filterEmergency,
  filterHighRisk,
  pageNumber = 1,
  pageSize = 50,
} = {}) => {
  const params = {};
  if (locationId != null) params.locationId = locationId;
  if (memberType) params.memberType = memberType;
  if (pcpName) params.pcpName = pcpName;
  if (gender) params.gender = gender;
  if (ageFrom != null) params.ageFrom = ageFrom;
  if (ageTo != null) params.ageTo = ageTo;
  if (dateOfBirth) params.dateOfBirth = dateOfBirth;
  if (memberId) params.memberId = memberId;
  if (memberFullName) params.memberFullName = memberFullName.toUpperCase();
  if (filterAdmitted != null) params.filterAdmitted = filterAdmitted;
  if (filterDischarged != null) params.filterDischarged = filterDischarged;
  if (filterEmergency != null) params.filterEmergency = filterEmergency;
  if (filterHighRisk != null) params.filterHighRisk = filterHighRisk;
  params.pageNumber = pageNumber;
  params.pageSize = pageSize;

  try {
    const response = await axios.get(API_URL, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      params,
    });

    if (response.status === 204 || !response.data) {
      return { data: [], page: pageNumber, pageSize };
    }
    return response.data;
  } catch (error) {
    // La request puede contener nombre/member ID en query; no exponer PHI.
    secureLogger.error("Error fetching patient map V2", error);
    throw error;
  }
};
